import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import {
  assistant,
  type AssistantMessage,
  type AssistantReply,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/Icon';

import { AssistantText } from './AssistantText';
import { useDialogoAberto } from './useDialogoAberto';

/**
 * Assistente do sistema.
 *
 * Um botao fixo no canto da tela abre a conversa em qualquer pagina. A tela aberta
 * segue junto da pergunta, para que "o que faco aqui?" tenha resposta. A conversa
 * fica so nesta aba do navegador: o servidor nao a guarda.
 *
 * Com uma janela aberta (Modal ou Drawer), o Assistente se desenha dentro do dialogo de
 * cima: fora dele, ficaria atras do fundo e inerte. Dentro do dialogo ele fica no mesmo
 * canto direito, mais alto, para nao cobrir os botoes do rodape da janela.
 * O Esc dentro do painel fecha so o painel; fora dele, continua fechando a janela.
 */

const CHAVE_DA_CONVERSA = 'seguracore.assistente.conversa';
const MAXIMO_GUARDADO = 40;
const MAXIMO_ENVIADO = 20;
const MAXIMO_PERGUNTA = 2000;
const MAXIMO_RESPOSTA_REENVIADA = 8000;

const SUGESTOES = [
  'O que faco nesta tela?',
  'Quais sao as etapas da esteira?',
  'O que o meu perfil permite fazer?',
];

interface MensagemDaConversa extends AssistantMessage {
  mode?: AssistantReply['mode'];
}

function conversaGuardada(): MensagemDaConversa[] {
  try {
    const bruto = sessionStorage.getItem(CHAVE_DA_CONVERSA);
    const lida: unknown = bruto ? JSON.parse(bruto) : [];
    return Array.isArray(lida) ? (lida as MensagemDaConversa[]) : [];
  } catch {
    return [];
  }
}

function guardarConversa(mensagens: MensagemDaConversa[]) {
  try {
    sessionStorage.setItem(
      CHAVE_DA_CONVERSA,
      JSON.stringify(mensagens.slice(-MAXIMO_GUARDADO)),
    );
  } catch {
    // Navegacao privativa pode recusar a escrita. A conversa continua na tela.
  }
}

export function AssistantWidget() {
  const location = useLocation();
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState<MensagemDaConversa[]>(conversaGuardada);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const fimDaConversa = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const dialogo = useDialogoAberto();
  const naJanela = dialogo !== null;

  const situacao = useQuery({
    queryKey: ['assistant', 'status'],
    queryFn: ({ signal }) => assistant.status(signal),
    enabled: aberto,
    staleTime: 60_000,
  });

  useEffect(() => {
    guardarConversa(mensagens);
  }, [mensagens]);

  // A conversa rola ate o fim a cada mensagem e quando o painel muda de lugar (para
  // dentro ou para fora de uma janela), porque a mudanca desenha o painel de novo.
  useEffect(() => {
    fimDaConversa.current?.scrollIntoView?.({ block: 'end' });
  }, [mensagens, dialogo, aberto]);

  async function perguntar(pergunta: string) {
    const limpa = pergunta.trim();
    if (!limpa || enviando) return;

    const comPergunta: MensagemDaConversa[] = [
      ...mensagens,
      { author: 'usuario', text: limpa },
    ];
    setMensagens(comPergunta);
    setTexto('');
    setFalha(null);
    setEnviando(true);
    try {
      const resposta = await assistant.send({
        messages: comPergunta.slice(-MAXIMO_ENVIADO).map((m) => ({
          author: m.author,
          text:
            m.author === 'assistente'
              ? m.text.slice(0, MAXIMO_RESPOSTA_REENVIADA)
              : m.text,
        })),
        screen: location.pathname,
      });
      setMensagens([
        ...comPergunta,
        { author: 'assistente', text: resposta.text, mode: resposta.mode },
      ]);
    } catch (erro) {
      setFalha(
        erro instanceof Error && erro.message
          ? erro.message
          : 'O assistente nao respondeu agora. Tente de novo em instantes.',
      );
    } finally {
      setEnviando(false);
    }
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void perguntar(texto);
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>) {
    if (evento.key === 'Enter' && !evento.shiftKey) {
      evento.preventDefault();
      void perguntar(texto);
    }
  }

  function novaConversa() {
    setMensagens([]);
    setFalha(null);
  }

  const ultimaPergunta = [...mensagens].reverse().find((m) => m.author === 'usuario');

  const assistente = (
    <>
      {aberto ? (
        <div
          role="dialog"
          aria-label="Assistente do SeguraCore"
          className={cn(
            'fixed inset-0 z-50 flex flex-col bg-surface text-left shadow-xl sm:inset-auto sm:bottom-20 sm:h-[34rem] sm:w-[26rem] sm:rounded-lg sm:border sm:border-line',
            naJanela ? 'sm:bottom-36 sm:right-6' : 'sm:right-6',
          )}
          onKeyDown={(evento) => {
            if (evento.key !== 'Escape') return;
            // Dentro de uma janela, o Esc tambem fecharia a janela. Cancelar a tecla
            // fecha so o painel, e o foco volta para o botao do Assistente.
            evento.preventDefault();
            evento.stopPropagation();
            setAberto(false);
            botao.current?.focus();
          }}
        >
          <header className="flex items-center gap-2 border-b border-line px-4 py-3">
            <Icon name="sparkle" />
            <h2 className="flex-1 text-sm font-semibold text-ink-800">Assistente</h2>
            <button
              type="button"
              className="rounded px-2 py-1 text-2xs text-ink-600 hover:bg-ink-50"
              onClick={novaConversa}
            >
              Nova conversa
            </button>
            <button
              type="button"
              aria-label="Fechar o assistente"
              className="rounded p-1 text-ink-600 hover:bg-ink-50"
              onClick={() => setAberto(false)}
            >
              <Icon name="close" />
            </button>
          </header>

          {situacao.data && !situacao.data.engine_connected ? (
            <p className="border-b border-line bg-ink-50 px-4 py-2 text-2xs text-ink-600">
              O motor de IA ainda nao esta conectado. As respostas vem direto da base de
              conhecimento do SeguraCore.
            </p>
          ) : null}

          <div className="flex-1 overflow-y-auto px-4 py-3 text-sm text-ink-800">
            {mensagens.length === 0 ? (
              <div className="flex flex-col gap-3">
                <p className="text-ink-600">
                  Pergunte como o SeguraCore funciona: telas, etapas da esteira,
                  permissoes, documentos e regras.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGESTOES.map((sugestao) => (
                    <button
                      key={sugestao}
                      type="button"
                      className="rounded-full border border-line px-3 py-1 text-2xs text-ink-700 hover:bg-ink-50"
                      onClick={() => void perguntar(sugestao)}
                    >
                      {sugestao}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ol className="flex flex-col gap-3">
                {mensagens.map((mensagem, indice) => (
                  <li
                    key={indice}
                    className={cn(
                      'max-w-[90%] rounded-lg px-3 py-2',
                      mensagem.author === 'usuario'
                        ? 'self-end bg-brand-500 text-white'
                        : 'self-start bg-ink-50',
                    )}
                  >
                    {mensagem.author === 'usuario' ? (
                      <p className="whitespace-pre-wrap">{mensagem.text}</p>
                    ) : (
                      <AssistantText text={mensagem.text} />
                    )}
                  </li>
                ))}
              </ol>
            )}

            {enviando ? (
              <p role="status" className="mt-3 text-2xs text-ink-500">
                O assistente esta respondendo...
              </p>
            ) : null}

            {falha ? (
              <div
                role="alert"
                className="mt-3 flex flex-col gap-2 text-2xs text-danger-700"
              >
                <p>{falha}</p>
                {ultimaPergunta ? (
                  <button
                    type="button"
                    className="self-start rounded border border-line px-2 py-1 text-ink-700 hover:bg-ink-50"
                    onClick={() => {
                      setMensagens((atual) => atual.slice(0, -1));
                      void perguntar(ultimaPergunta.text);
                    }}
                  >
                    Tentar de novo
                  </button>
                ) : null}
              </div>
            ) : null}
            <div ref={fimDaConversa} />
          </div>

          <form
            onSubmit={enviar}
            className="flex flex-col gap-1 border-t border-line p-3"
          >
            <label htmlFor="pergunta-ao-assistente" className="sr-only">
              Pergunta ao assistente
            </label>
            <div className="flex items-end gap-2">
              <textarea
                id="pergunta-ao-assistente"
                rows={2}
                maxLength={MAXIMO_PERGUNTA}
                value={texto}
                placeholder="Escreva sua pergunta"
                className="min-h-[2.5rem] flex-1 resize-none rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                onChange={(evento) => setTexto(evento.target.value)}
                onKeyDown={aoTeclar}
              />
              <button
                type="submit"
                aria-label="Enviar pergunta"
                disabled={enviando || texto.trim() === ''}
                className="rounded-md bg-brand-500 p-2 text-white disabled:opacity-50"
              >
                <Icon name="send" />
              </button>
            </div>
            {texto.length > MAXIMO_PERGUNTA * 0.8 ? (
              <p className="text-right text-2xs text-ink-500">
                {texto.length} de {MAXIMO_PERGUNTA} caracteres
              </p>
            ) : null}
            <p className="text-2xs text-ink-500">
              Respostas com base na documentacao do SeguraCore e no que o seu perfil
              permite ver.
            </p>
          </form>
        </div>
      ) : null}

      <button
        ref={botao}
        type="button"
        aria-label={aberto ? 'Fechar o assistente' : 'Abrir o assistente'}
        aria-expanded={aberto}
        className={cn(
          'fixed z-50 flex size-12 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg hover:bg-brand-600',
          naJanela ? 'bottom-20 right-6' : 'bottom-6 right-6',
        )}
        onClick={() => setAberto((atual) => !atual)}
      >
        <Icon name={aberto ? 'close' : 'sparkle'} size={22} />
      </button>
    </>
  );

  return dialogo ? createPortal(assistente, dialogo) : assistente;
}
